import { roundMoney } from './billing-product.type';

/** One line on a patient bill. The name and price are copied from the product at save time. */
export interface BillItem {
  productId: string;
  name: string;
  quantity: number;
  unitPrice: number;
}

/** Document at businesses/{businessId}/patients/{patientId}/bills/{billId}. */
export interface PatientBill {
  id: string;
  patientId: string;
  patientName: string;
  doctorUid: string;
  doctorName: string;
  /** Calendar date, YYYY-MM-DD, in the clinic user's local timezone. */
  dueDate: string;
  items: BillItem[];
  createdBy: string;
}

const DUE_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const MAX_ITEMS = 40;
const MAX_DESCRIPTION = 120;

export function todayDateInputValue(now = new Date()): string {
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

export function isBillDueDate(value: string): boolean {
  const match = DUE_DATE.exec(value);
  if (!match) {
    return false;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
}

export function formatBillDueDate(dueDate: string): string {
  const match = DUE_DATE.exec(dueDate);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : dueDate;
}

function lineKey(item: Pick<BillItem, 'productId' | 'unitPrice'>): string {
  return `${item.productId}:${roundMoney(item.unitPrice).toFixed(2)}`;
}

/** Adds one unit, merging it into an existing line for the same product and price. */
export function groupBillItem(items: BillItem[], addition: Omit<BillItem, 'quantity'>): BillItem[] {
  const name = addition.name.trim().replace(/\s+/g, ' ').slice(0, MAX_DESCRIPTION);
  const productId = addition.productId.trim();
  const unitPrice = roundMoney(addition.unitPrice);
  if (!name || !productId || !Number.isFinite(unitPrice) || unitPrice < 0) {
    return items;
  }
  const key = lineKey({ productId, unitPrice });
  const index = items.findIndex((item) => lineKey(item) === key);
  if (index >= 0) {
    return items.map((item, i) =>
      i === index ? { ...item, quantity: item.quantity + 1 } : item
    );
  }
  return [...items, { productId, name, quantity: 1, unitPrice }];
}

/** Drops empty lines and sums quantities of the same product at the same price. */
export function collapseBillItems(items: BillItem[]): BillItem[] {
  const grouped: BillItem[] = [];
  for (const item of items) {
    const name = item.name.trim().replace(/\s+/g, ' ').slice(0, MAX_DESCRIPTION);
    const productId = item.productId.trim();
    const quantity = Math.floor(Number(item.quantity));
    const unitPrice = roundMoney(Number(item.unitPrice));
    if (!name || !productId || !Number.isFinite(quantity) || quantity < 1) {
      continue;
    }
    if (!Number.isFinite(unitPrice) || unitPrice < 0) {
      continue;
    }
    const key = lineKey({ productId, unitPrice });
    const existing = grouped.find((row) => lineKey(row) === key);
    if (existing) {
      existing.quantity += quantity;
    } else {
      grouped.push({ productId, name, quantity, unitPrice });
    }
  }
  if (grouped.length > MAX_ITEMS) {
    throw new Error(`Una factura puede tener hasta ${MAX_ITEMS} conceptos.`);
  }
  return grouped;
}

export function lineTotal(item: BillItem): number {
  return roundMoney(item.quantity * item.unitPrice);
}

export function billTotal(items: readonly BillItem[]): number {
  return roundMoney(items.reduce((sum, item) => sum + lineTotal(item), 0));
}

/** Billed minus paid. Positive is money still owed; negative is credit. */
export function patientAccountBalance(
  bills: readonly { items: readonly BillItem[] }[],
  payments: readonly { amount: number }[]
): { billed: number; paid: number; difference: number } {
  const billed = roundMoney(bills.reduce((sum, bill) => sum + billTotal(bill.items), 0));
  const paid = roundMoney(payments.reduce((sum, payment) => sum + roundMoney(payment.amount), 0));
  return { billed, paid, difference: roundMoney(billed - paid) };
}

/** Copies the catalog price onto lines whose product price is not editable. */
export function priceBillItems(
  items: BillItem[],
  products: readonly { id: string; name: string; price: number; priceEditable: boolean }[]
): BillItem[] {
  const priced = items.map((item) => {
    const product = products.find((candidate) => candidate.id === item.productId);
    if (!product) {
      throw new Error('Ese producto ya no está disponible.');
    }
    const unitPrice = product.priceEditable ? roundMoney(item.unitPrice) : product.price;
    if (!Number.isFinite(unitPrice) || unitPrice < 0) {
      throw new Error('El precio no es válido.');
    }
    return {
      productId: product.id,
      name: product.name,
      quantity: item.quantity,
      unitPrice,
    };
  });
  return collapseBillItems(priced);
}
